import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import DeleteIcon from "@material-ui/icons/Delete";

import { StyledTableCell, useInvoiceStyles, formatMoney } from "../styles";
import InvoiceSummary from "./InvoiceSummary";

/**
 * Manage Lots -- shows every lot on an invoice and lets one be removed.
 *
 * Presentational only. The parent owns the data and does the API call,
 * so this file has nothing to mock when testing it.
 */
const ManageLotsDialog = ({
  open,
  loading,
  invoice,
  rows,
  removing,
  onRemove,
  onClose,
}) => {
  const classes = useInvoiceStyles();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Manage Lots</DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ minHeight: 250 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <InvoiceSummary invoice={invoice} />

            <Divider />

            <Box mt={3}>
              <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, marginBottom: 12 }}
              >
                Lots on this invoice
              </Typography>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>Lot No.</StyledTableCell>
                      <StyledTableCell>Artist</StyledTableCell>
                      <StyledTableCell>Title</StyledTableCell>
                      <StyledTableCell align="right">Hammer</StyledTableCell>
                      <StyledTableCell align="right">Premium</StyledTableCell>
                      <StyledTableCell align="right">Total</StyledTableCell>
                      <StyledTableCell align="center">Action</StyledTableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.length > 0 ? (
                      rows.map((item) => (
                        <TableRow key={item.id || item.lot_number}>
                          <StyledTableCell style={{ fontWeight: 600 }}>
                            {item.lot_number}
                          </StyledTableCell>

                          <StyledTableCell>
                            {item.artist || "---"}
                          </StyledTableCell>

                          <StyledTableCell>
                            {item.title || "---"}
                          </StyledTableCell>

                          <StyledTableCell align="right">
                            {formatMoney(item.hammer_price)}
                          </StyledTableCell>

                          <StyledTableCell align="right">
                            {formatMoney(item.buyer_premium)}
                          </StyledTableCell>

                          <StyledTableCell align="right">
                            {formatMoney(item.total_price)}
                          </StyledTableCell>

                          <StyledTableCell align="center">
                            <IconButton
                              size="small"
                              className={classes.deleteButton}
                              onClick={() => onRemove(item.lot_number)}
                              // The last lot cannot be removed -- the backend
                              // refuses it too. Disabling here saves a round
                              // trip and explains itself through the title.
                              disabled={rows.length <= 1 || removing !== null}
                              title={
                                rows.length <= 1
                                  ? "Cancel the invoice instead of removing its last lot"
                                  : "Remove this lot"
                              }
                            >
                              {String(removing) === String(item.lot_number) ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </StyledTableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <StyledTableCell colSpan={7} align="center">
                          No lots on this invoice
                        </StyledTableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {Number(invoice?.paid_amount || 0) > 0 && (
                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    marginTop: 10,
                    color: "#b26a00",
                  }}
                >
                  This invoice already has payments against it. Lots can only
                  be removed while the new total stays at or above the amount
                  paid.
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

ManageLotsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  invoice: PropTypes.object,
  rows: PropTypes.array,
  removing: PropTypes.any,
  onRemove: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

ManageLotsDialog.defaultProps = {
  loading: false,
  rows: [],
  removing: null,
};

export default ManageLotsDialog;
